import React, { useState } from "react";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";
import {
  Upload,
  File,
  Download,
  Eye,
  Trash2,
  Paperclip,
  ShieldCheck,
} from "lucide-react";
import { FormField, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { useConfirm } from "../../hooks/useConfirm";
import type {
  AttachmentCategory,
  ContainerOrderAttachment,
  ContainerOrderFormData,
} from "../../types/containerOrders";
import { PreviewableExtensions } from "../../constants/formOptions";
import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_FILES_PER_ZONE,
  ATTACHMENT_MAX_FILE_SIZE_MB,
} from "../../constants/containerOrderConstants";
import {
  deliverAttachment,
  extensionOf,
  screenAttachments,
} from "../../utils/containerAttachments";
import { containerOrderAPI } from "../../services/api";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

interface OrderAttachmentsSectionProps {
  register: UseFormRegister<ContainerOrderFormData>;
  setValue: UseFormSetValue<ContainerOrderFormData>;
  readonly: boolean;
  newAttachments: File[];
  setNewAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  newCustomsAttachments: File[];
  setNewCustomsAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  orderId?: string;
  storedAttachments: ContainerOrderAttachment[];
  /** `updatedAt` is the order's fresh version token, minted by the delete. */
  onAttachmentDeleted: (attachmentId: string, updatedAt?: string) => void;
}

/**
 * Section 7 — attachments & documents, shared across all shipments.
 *
 * Two drop zones, as in the mockup:
 *   • General attachments — contract, PO, correspondence
 *   • Customs documents   — master B/L, HS codes, export declaration
 *
 * Customs paperwork is kept as its own category rather than mixed in, because
 * it is exactly what a customs audit asks for; tagging it at upload time means
 * it never has to be identified by filename later.
 *
 * Deliberately simpler than the shipment form's attachments block: an order has
 * no driver uploads, no client-facing public toggle and no signed documents —
 * those belong to an individual shipment's lifecycle, not to the batch booking.
 * Files stay on the order rather than being copied onto each generated
 * shipment, so deleting one child never removes a document the others share.
 */
export function OrderAttachmentsSection({
  register,
  setValue,
  readonly,
  newAttachments,
  setNewAttachments,
  newCustomsAttachments,
  setNewCustomsAttachments,
  orderId,
  storedAttachments,
  onAttachmentDeleted,
}: OrderAttachmentsSectionProps) {
  const { t } = useTranslation("containerOrders");
  const { confirm, confirmDialog } = useConfirm();
  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Screen a pick against the three rules, and say what was dropped and why.
   *
   * The rules themselves live in `screenAttachments`, shared with the container
   * page and the driver's screen so all three accept the same files; this only
   * turns its verdict into sentences.
   */
  const acceptable = (selected: File[], room: number) => {
    const { kept, rejected } = screenAttachments(selected, room);

    for (const rejection of rejected) {
      toast.error(
        rejection.reason === "type"
          ? t("attachments.rejectedType", { name: rejection.name })
          : rejection.reason === "size"
            ? t("attachments.rejectedSize", {
                name: rejection.name,
                limit: ATTACHMENT_MAX_FILE_SIZE_MB,
              })
            : t("attachments.rejectedCount", {
                name: rejection.name,
                limit: ATTACHMENT_MAX_FILES_PER_ZONE,
              }),
      );
    }

    return kept;
  };

  const pickerFor = (category: AttachmentCategory) => {
    const isCustoms = category === "customs";
    const setter = isCustoms ? setNewCustomsAttachments : setNewAttachments;
    const field = isCustoms ? "customsAttachments" : "attachments";

    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files || []);
      event.target.value = "";

      // Screened outside the state updater: `acceptable` raises a toast per
      // rejected file, and React re-runs updaters in StrictMode — which would
      // show every one of those warnings twice.
      const current = isCustoms ? newCustomsAttachments : newAttachments;
      const kept = acceptable(
        selected,
        ATTACHMENT_MAX_FILES_PER_ZONE - current.length,
      );
      if (!kept.length) return;

      const updated = [...current, ...kept];
      setter(updated);
      setValue(field, updated, { shouldValidate: true });
    };
  };

  const removePending = (category: AttachmentCategory, index: number) => {
    const isCustoms = category === "customs";
    const setter = isCustoms ? setNewCustomsAttachments : setNewAttachments;
    const field = isCustoms ? "customsAttachments" : "attachments";

    setter((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      setValue(field, updated, { shouldValidate: true });
      return updated;
    });
  };

  const isPreviewable = (name: string) =>
    PreviewableExtensions.includes(extensionOf(name));

  const handleDownload = async (
    attachment: ContainerOrderAttachment,
    previewOnly = false,
  ) => {
    if (!orderId) return;

    try {
      setBusyId(attachment._id);
      const res = await containerOrderAPI.downloadAttachment(
        orderId,
        attachment._id,
      );
      deliverAttachment(res, attachment, previewOnly);
    } catch {
      toast.error(t("attachments.downloadFailed"));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Remove one stored document.
   *
   * Confirmed first: it is the only control in this block that takes effect
   * before the form is saved, and the file is gone from disk by the time the
   * button releases — abandoning the edit will not bring it back.
   */
  const handleDelete = async (attachment: ContainerOrderAttachment) => {
    if (!orderId) return;

    const agreed = await confirm({
      title: t("confirm.titles.deleteAttachment"),
      description: t("attachments.confirmDelete", {
        name: attachment.originalName,
      }),
      confirmLabel: t("confirm.actions.deleteAttachment"),
      cancelLabel: t("confirm.actions.keep"),
      tone: "danger",
    });
    if (!agreed) return;

    try {
      setBusyId(attachment._id);
      const res = await containerOrderAPI.deleteAttachment(
        orderId,
        attachment._id,
      );
      // The token this delete just minted goes back to the form — the write
      // moved the order's `updatedAt`, and a save straight afterwards would
      // otherwise be refused as somebody else's change.
      onAttachmentDeleted(attachment._id, res.data?.data?.updatedAt);
      toast.success(t("attachments.deleted"));
    } catch {
      toast.error(t("attachments.deleteFailed"));
    } finally {
      setBusyId(null);
    }
  };

  /** One drop zone plus the files already in (or headed for) its category. */
  const zone = (category: AttachmentCategory) => {
    const isCustoms = category === "customs";
    // Legacy rows predate the category field — treat untagged as general.
    const stored = storedAttachments.filter((a) =>
      isCustoms ? a.category === "customs" : a.category !== "customs",
    );
    const pending = isCustoms ? newCustomsAttachments : newAttachments;

    const Icon = isCustoms ? ShieldCheck : Upload;

    return (
      <div className="space-y-2">
        <label
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-5 text-center transition-colors ${
            isCustoms
              ? "border-honey/50 bg-honey/8 hover:border-honey"
              : "border-dark/15 bg-bone/60 hover:border-dark/30"
          } ${readonly ? "pointer-events-none opacity-60" : ""}`}
        >
          <Icon
            className={`mb-1 h-5 w-5 ${isCustoms ? "text-honey" : "text-mist"}`}
          />
          <span
            className={`text-[12px] font-semibold ${
              isCustoms ? "text-amber-900" : "text-ink"
            }`}
          >
            {t(
              isCustoms
                ? "attachments.customsLabel"
                : "attachments.generalLabel",
            )}
          </span>
          <span
            className={`text-[10.5px] leading-snug ${
              isCustoms ? "text-amber-700" : "text-mist"
            }`}
          >
            {t(isCustoms ? "attachments.customsHint" : "attachments.generalHint")}
          </span>
          {/* The limits are stated up front rather than discovered by hitting
              them — the picker enforces exactly these. */}
          <span className="mt-1 text-[10px] text-mist">
            {t("attachments.limitsHint", {
              count: ATTACHMENT_MAX_FILES_PER_ZONE,
              size: ATTACHMENT_MAX_FILE_SIZE_MB,
            })}
          </span>
          <input
            type="file"
            multiple
            // A hint to the OS dialog only; `acceptable` is what enforces it.
            accept={ATTACHMENT_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",")}
            className="hidden"
            disabled={readonly}
            onChange={pickerFor(category)}
          />
        </label>

        {(stored.length > 0 || pending.length > 0) && (
          <ul className="divide-y divide-dark/8 rounded-md border border-dark/10">
            {stored.map((attachment) => (
              <li
                key={attachment._id}
                className="flex items-center gap-2 px-2.5 py-1.5"
              >
                <File
                  className={`h-3.5 w-3.5 shrink-0 ${
                    isCustoms ? "text-honey" : "text-mist"
                  }`}
                />
                <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink">
                  {attachment.originalName}
                </span>
                <span className="shrink-0 text-[10.5px] text-mist">
                  {(attachment.size / 1024).toFixed(0)} KB
                </span>

                {isPreviewable(attachment.originalName) && (
                  <button
                    type="button"
                    disabled={busyId === attachment._id}
                    onClick={() => handleDownload(attachment, true)}
                    className="rounded p-1 text-mist hover:bg-bone hover:text-ink"
                    title={t("attachments.preview")}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                )}

                <button
                  type="button"
                  disabled={busyId === attachment._id}
                  onClick={() => handleDownload(attachment)}
                  className="rounded p-1 text-mist hover:bg-bone hover:text-ink"
                  title={t("attachments.download")}
                >
                  <Download className="h-3.5 w-3.5" />
                </button>

                {!readonly && (
                  <button
                    type="button"
                    disabled={busyId === attachment._id}
                    onClick={() => handleDelete(attachment)}
                    className="rounded p-1 text-signal hover:bg-signal/10"
                    title={t("attachments.delete")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}

            {pending.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 px-2.5 py-1.5"
              >
                <File className="h-3.5 w-3.5 shrink-0 text-mist" />
                <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink">
                  {file.name}
                </span>
                <span className="shrink-0 rounded bg-bone px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-mist">
                  {t("attachments.pending")}
                </span>
                <button
                  type="button"
                  onClick={() => removePending(category, index)}
                  className="rounded p-1 text-signal hover:bg-signal/10"
                  title={t("attachments.delete")}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <SectionCard icon={Paperclip} title={t("sections.attachments")}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {zone("general")}
          {zone("customs")}
        </div>

        <FormField label={t("attachments.notesLabel")} size="sm">
          <Textarea
            {...register("additionalNotes")}
            rows={3}
            readOnly={readonly}
            placeholder={t("placeholders.notes")}
          />
        </FormField>
      </div>

      {/* Portalled to the body, so its buttons are outside the order form. */}
      {confirmDialog}
    </SectionCard>
  );
}
